import CancelOutlined from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import DraftsOutlined from '@mui/icons-material/DraftsOutlined';
import EditNoteOutlined from '@mui/icons-material/EditNoteOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import FileCopyOutlined from '@mui/icons-material/FileCopyOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import PeopleOutlineOutlined from '@mui/icons-material/PeopleOutlineOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import ReceiptOutlined from '@mui/icons-material/ReceiptOutlined';
import SmsOutlined from '@mui/icons-material/SmsOutlined';
import WarningRounded from '@mui/icons-material/WarningRounded';
import {
  buttonBaseClasses,
  svgIconClasses,
  tooltipClasses,
  useTheme,
} from '@mui/material';
import Box from '@mui/material/Box';
import { buttonClasses, ButtonProps } from '@mui/material/Button';
import MUIChip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  OrderState,
  OrderStateEvent,
  putV1CompaniesCompanyIdJobsBulkAccept,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil, partition } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import React, {
  cloneElement,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import HighlightedText from '~components/HighlightedText/HighlightedText';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import UnreadCountBadge from '~components/Messaging/UnreadCountBadge';
import {
  FlexColumn,
  OverflowAwareText,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import OrderStatusTransitioner from '~components/Order/OrderStatusTransitioner';
import OriginalEmailDialog from '~components/Order/OriginalEmailDialog';
import OrderProgressWidget from '~components/OrderProgressWidget/OrderProgressWidget';
import { useOrderSearchHighlightTerm } from '~components/OrderSearchHighlightContext/OrderSearchHighlightContext';
import { NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX } from '~constants/filters';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import JobCardPdfTypeDialog from '~hooks/useJobCardPdf/JobCardPdfTypeDialog';
import { JobCardPdfRecipientType } from '~hooks/useJobCardPdf/models';
import { LoadDigestLoadFilter } from '~hooks/useLoadDigests/models';
import { Order } from '~hooks/useOrders/models';
import { Project } from '~hooks/useProjects/models';
import { GeofenceStartAdornment } from '~pages/Settings/Administration/Sites/components/GeofenceStartAdornment';
import useMessagingOrderUnreadCountQuery from '~queries/useMessagingOrderUnreadCountQuery';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import formatNumber from '~utils/formatNumber';
import { getAcceptableJobs, isJobAssignable } from '~utils/jobs/job-utils';
import {
  extractWaypointData,
  formatContactText,
  formatMaterialRowLabels,
  formatOrderStartTime,
  getDeliveredByMaterialAndUnit,
  getDeliveredForMaterialRow,
  getOrderDeliveredLoads,
  getOrderMaterialRows,
  getOrderNotes,
  getOrderTicketsSummary,
  getOrderTrucksProgress,
  makeOrderedQuantityText,
  orderShowsMaterialList,
} from '~utils/orders';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { doneOrderStates } from '../constants/orderStates';
import { getJobCycleTimeText } from './dispatchExportColumns';
import getDownloadJobCardPdfsContent from './downloadJobCardPdfsContent';
import LoadDigestViewDialog from './LoadDigest/LoadDigestViewDialog';
import useLoadDigestAccess from './LoadDigest/useLoadDigestAccess';

interface OrderDispatchCardFragment {
  order: Order;
}

const ORDER_EVENT_TO_STATE_MAP: Record<OrderStateEvent, OrderState> = {
  [OrderStateEvent.CANCEL]: OrderState.CANCELED,
  [OrderStateEvent.COMPLETE]: OrderState.COMPLETED,
  [OrderStateEvent.REJECT]: OrderState.REJECTED,
  [OrderStateEvent.ACCEPT]: OrderState.ACCEPTED,
  [OrderStateEvent.START]: OrderState.IN_PROGRESS,
  [OrderStateEvent.REQUEST]: OrderState.PENDING_REQUEST,
  [OrderStateEvent.HOLD]: OrderState.HOLD,
  [OrderStateEvent.WEATHER_HOLD]: OrderState.WEATHER_HOLD,
} as const;

export function RejectReasonBadge({ order }: OrderDispatchCardFragment) {
  if (!order.rejectReason) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mt: 0.25,
        maxWidth: '200px',
      }}
    >
      <Tooltip
        title={order.rejectReason}
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, -8],
                },
              },
            ],
          },
        }}
      >
        <MUIChip
          label={order.rejectReason}
          size="small"
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            borderRadius: '4px',
          }}
          color="error"
          variant="outlined"
        />
      </Tooltip>
    </Box>
  );
}

interface StatusProps extends OrderDispatchCardFragment {
  onAccept: () => void;
  onOrderStateChange: (nextOrderState: OrderState) => Promise<void>;
  onReject: () => void;
  onCancel: () => void;
}

type AvailableAction = {
  color: ButtonProps['color'];
  icon?: ReactNode;
  isDisabled?: boolean;
  label: string;
  onClick: () => void;
  variant?: ButtonProps['variant'];
  sx?: ButtonProps['sx'];
};

export const Status = observer(
  ({ onAccept, onOrderStateChange, onReject, onCancel, order }: StatusProps) => {
    const { ordersDispatchStore } = useStores();

    const [orderNextState, setOrderNextState] = useState<OrderState>();

    const orderCTAs = useMemo(() => {
      const isPendingRequest = order.state === OrderState.PENDING_REQUEST;

      if (isPendingRequest && order.editable) {
        return [
          {
            color: 'brandV2Green',
            isDisabled: ordersDispatchStore.acceptingOrRejectingOrderId === order.id,
            label: t('actions.accept'),
            onClick: onAccept,
          } as AvailableAction,
          {
            color: 'brandV2Red',
            isDisabled: ordersDispatchStore.acceptingOrRejectingOrderId === order.id,
            label: t('actions.reject'),
            onClick: onReject,
          } as AvailableAction,
          {
            color: 'brandV2Red',
            isDisabled: ordersDispatchStore.acceptingOrRejectingOrderId === order.id,
            label: t('actions.cancel'),
            onClick: onCancel,
            variant: 'text',
            icon: <CancelOutlined />,
            sx: { mx: 1 },
          } as AvailableAction,
        ];
      }

      return null;
    }, [order.id, order.state, ordersDispatchStore.acceptingOrRejectingOrderId]);

    const transitionableStates: OrderState[] = useMemo(() => {
      if (!order.editable) {
        return [];
      }
      return order.stateEvents.map((stateEvent) => {
        return ORDER_EVENT_TO_STATE_MAP[stateEvent as OrderStateEvent];
      });
    }, [order.editable, order.stateEvents]);

    const isTransitioningToStatus = useMemo(() => {
      if (ordersDispatchStore.isCancellingOrder) {
        return OrderState.CANCELED;
      }

      if (ordersDispatchStore.isCompletingOrder) {
        return OrderState.COMPLETED;
      }

      return orderNextState;
    }, [
      orderNextState,
      ordersDispatchStore.isCancellingOrder,
      ordersDispatchStore.isCompletingOrder,
    ]);

    const handleOrderStateChange = useCallback<typeof onOrderStateChange>(
      async (state) => {
        setOrderNextState(state);
        await onOrderStateChange(state);
        setOrderNextState(undefined);
      },
      [onOrderStateChange],
    );

    if (orderCTAs) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {orderCTAs.map((cta) => (
            <SmallButton
              key={cta.label}
              color={cta.color}
              disabled={cta.isDisabled}
              onClick={(event) => {
                event.stopPropagation();
                cta.onClick();
              }}
              startIcon={cta.icon}
              variant={cta.variant}
              sx={{
                [`&.${buttonBaseClasses.root}`]: {
                  height: '24px',
                  px: 0.5,
                  [`&.${buttonBaseClasses.disabled}`]: { pointerEvents: 'auto' },
                  [`.${svgIconClasses.root}`]: { fontSize: '16px' },
                },
                ...cta.sx,
              }}
            >
              {cta.label}
            </SmallButton>
          ))}
        </Box>
      );
    }

    return (
      <Box>
        <OrderStatusTransitioner
          closeOnTransitionComplete
          isTransitioningToStatus={isTransitioningToStatus}
          onTransitionToStatus={handleOrderStateChange}
          order={order}
          transitionableStates={transitionableStates}
        />
      </Box>
    );
  },
);

interface BulkAssignMultipleCTAProps extends OrderDispatchCardFragment {
  onClick?: () => void;
}

export const BulkAssignMultipleCTA = observer(
  ({ onClick, order }: BulkAssignMultipleCTAProps) => {
    const theme = useTheme();
    const { ordersDispatchStore, userStore } = useStores();
    const isDone = doneOrderStates.includes(order.state);
    const orderJobs = ordersDispatchStore.orderJobsSnapshot.get(order.id);

    const shouldShowCTA = useMemo(() => {
      if (!onClick || isDone) {
        return false;
      }

      const orderHasAssignableJobs = (orderJobs ?? []).some((job) =>
        isJobAssignable(job),
      );

      return orderHasAssignableJobs;
    }, [orderJobs, isDone, onClick]);

    if (!shouldShowCTA || userStore.isViewOnlyUser) {
      return null;
    }

    return (
      <Tooltip title={t('dispatch.order.bulk_assign_order')}>
        <Box
          sx={{
            display: 'inline-block',
            height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
          }}
        >
          <SmallButton
            color="brandV2Orange"
            variant="text"
            startIcon={<PeopleOutlineOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              AnalyticsController.track('order_bulk_assign_opened', {
                order_id: order.id,
                order_friendly_id: order.dispatchNumber?.name || '',
                assignable_job_count:
                  ordersDispatchStore.orderJobs
                    .get(order.id)
                    ?.filter((job) => isJobAssignable(job)).length || 0,
              });
              onClick?.();
            }}
            sx={{
              [`&.${buttonBaseClasses.root}`]: {
                ...orderActionButtonSx(theme.brandV2.colors.treadOrange),
                borderRadius: '50%',
                minWidth: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                p: 0,
                width: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
              },
            }}
          />
        </Box>
      </Tooltip>
    );
  },
);

export const AcceptAllJobsCTA = observer(({ order }: { order: Order }) => {
  const { ordersDispatchStore, userStore, toasterStore } = useStores();
  const [isLoading, setIsLoading] = React.useState(false);

  const companyId = getEffectiveUserCompanyId(userStore);
  const orderJobs = ordersDispatchStore.orderJobsSnapshot.get(order.id);

  const orderAcceptableJobs = useMemo(() => {
    return getAcceptableJobs(orderJobs ?? []);
  }, [orderJobs]);

  const shouldShowCTA = useMemo(() => {
    if (userStore.isViewOnlyUser) {
      return false;
    }

    return orderAcceptableJobs.length > 0;
  }, [userStore.isViewOnlyUser, orderAcceptableJobs]);

  const handleAcceptAll = async (event: React.MouseEvent) => {
    event.stopPropagation();

    if (isLoading || orderAcceptableJobs.length === 0) {
      return;
    }

    try {
      setIsLoading(true);

      const jobIds = orderAcceptableJobs.map((job) => job.id);

      await putV1CompaniesCompanyIdJobsBulkAccept({
        path: { 'company-id': companyId },
        body: { job_ids: jobIds },
      });

      ordersDispatchStore.setJobsWaitingForRTU(jobIds);

      toasterStore.push(
        alert(t('dispatch.order.accept_all_success_enqueued'), AlertTypes.success),
      );
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_bulk_accept_jobs'),
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!shouldShowCTA) {
    return null;
  }

  return (
    <Tooltip title={t('dispatch.order.accept_all_jobs')}>
      <Box
        sx={{
          display: 'inline-block',
          height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
        }}
      >
        <SmallLoadingButton
          color="brandV2Green"
          loading={isLoading}
          onClick={handleAcceptAll}
        >
          {t('dispatch.order.accept_all_jobs_cta')}
        </SmallLoadingButton>
      </Box>
    </Tooltip>
  );
});

interface OrderUnreadMessagesCTAProps extends OrderDispatchCardFragment {
  onClick: () => void;
}

const orderActionButtonSx = (hoverBorderColor: string) => ({
  border: 'solid 1px transparent',
  height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
  '&:hover': { borderColor: hoverBorderColor },
  [`.${buttonClasses.startIcon}`]: { mx: 0 },
});

export const OrderUnreadMessagesCTA = observer(
  ({ onClick, order }: OrderUnreadMessagesCTAProps) => {
    const theme = useTheme();
    const { unreadCount: orderUnreadCount } = useMessagingOrderUnreadCountQuery(order);

    if (orderUnreadCount === 0) {
      return null;
    }

    return (
      <Tooltip title={t('messaging.order_tab.open_unread', { count: orderUnreadCount })}>
        <Box
          sx={{
            display: 'inline-block',
            height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
          }}
        >
          <SmallButton
            color="brandV2TreadGray3"
            data-test-id={`page-dispatch__order-${order.orderId}__unread-messages-button`}
            onClick={(event) => {
              event.stopPropagation();
              onClick();
            }}
            startIcon={<ForumOutlined />}
            sx={{
              [`&.${buttonBaseClasses.root}`]: {
                ...orderActionButtonSx(theme.brandV2.colors.treadGray3),
                gap: 0.5,
                minWidth: 0,
                px: 0.75,
              },
            }}
            variant="text"
          >
            <UnreadCountBadge count={orderUnreadCount} />
          </SmallButton>
        </Box>
      </Tooltip>
    );
  },
);

interface EditOrderCTAProps extends OrderDispatchCardFragment {
  isEditInProgress?: boolean;
  onClick: () => void;
}

export const EditOrderCTA = observer(
  ({ isEditInProgress = false, onClick, order }: EditOrderCTAProps) => {
    const theme = useTheme();

    if (!order.editable) {
      return null;
    }

    return (
      <Tooltip
        title={
          isEditInProgress
            ? t('dispatch.dispatch_v2.resume_new_order')
            : t('dispatch.order.edit_order')
        }
      >
        <Box
          sx={{
            display: 'inline-block',
            height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
          }}
        >
          <SmallButton
            color="brandV2Orange"
            data-test-id={`page-dispatch__order-${order.orderId}__edit-button`}
            variant={isEditInProgress ? 'contained' : 'text'}
            startIcon={isEditInProgress ? <EditNoteOutlined /> : <EditOutlined />}
            onClick={(event) => {
              event.stopPropagation();
              onClick();
            }}
            sx={{
              [`&.${buttonBaseClasses.root}`]: {
                border: 'solid 1px transparent',
                borderRadius: '50%',
                height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                minWidth: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                p: 0,
                width: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                '&:hover': { borderColor: theme.brandV2.colors.treadOrange },
                [`.${buttonClasses.startIcon}`]: { mx: 0 },
              },
            }}
          />
        </Box>
      </Tooltip>
    );
  },
);

interface ThreeDotsMenuProps extends OrderDispatchCardFragment {
  onCancelClick: () => void;
  onCloneOrderClick: () => void;
  onCopyAndEditClick: () => void;
  onDownloadFilesClick?: () => void;
  onDownloadJobCardPdfsClick?: (recipientType: JobCardPdfRecipientType) => void;
  onEmailLoadDigestClick?: () => void;
  onOpenMessagingTabClick?: () => void;
  onTextAllDriversClick: () => void;
  onViewLoadDigestClick?: (loadFilter: LoadDigestLoadFilter) => void;
}

export const ThreeDotsMenu = observer(
  ({
    onCancelClick,
    onCloneOrderClick,
    onCopyAndEditClick,
    onDownloadFilesClick,
    onDownloadJobCardPdfsClick,
    onEmailLoadDigestClick,
    onOpenMessagingTabClick,
    onTextAllDriversClick,
    onViewLoadDigestClick,
    order,
  }: ThreeDotsMenuProps) => {
    const theme = useTheme();
    const { userStore } = useStores();
    const {
      canCreateOrder,
      canCreateOrderRequest,
      canCreateJob,
      canDeleteOrder,
      canEditJob,
    } = userStore.getPermissions();
    const menuHandler = useRef<MenuHandler>(null);
    const [isJobCardPdfTypeDialogOpen, setIsJobCardPdfTypeDialogOpen] = useState(false);
    const [isLoadDigestViewDialogOpen, setIsLoadDigestViewDialogOpen] = useState(false);
    const [isOriginalEmailDialogOpen, setIsOriginalEmailDialogOpen] = useState(false);

    const { canEmail: canEmailLoadDigestPdf, canView: canViewLoadDigestPdf } =
      useLoadDigestAccess();

    const { editable, state } = order;

    const isCloneable = editable && (canCreateOrder || canCreateOrderRequest);
    const isDone = doneOrderStates.includes(state);

    const isCancellable =
      editable && (!isDone || state === OrderState.REJECTED) && canDeleteOrder;
    const canAddNewJobs = editable && !isDone && canCreateJob;
    const canTextDrivers = editable && !isDone;

    const canMessageAllDrivers = onOpenMessagingTabClick && order.messagingEnabled;

    const canDownloadJobCardPdfs = onDownloadJobCardPdfsClick && canEditJob;

    const hasLoadDigestHandlers = Boolean(
      onViewLoadDigestClick || onEmailLoadDigestClick,
    );
    // A load digest covers only orders the sending company owns, never received ones.
    // The effective company, not the user's own, because the digest requests are sent as it.
    const isLoadDigestOfferable =
      hasLoadDigestHandlers && order.company?.id === getEffectiveUserCompanyId(userStore);
    const shouldShowViewLoadDigestAction =
      isLoadDigestOfferable && canViewLoadDigestPdf && onViewLoadDigestClick;
    const shouldShowEmailLoadDigestAction =
      isLoadDigestOfferable && canEmailLoadDigestPdf && onEmailLoadDigestClick;

    const canViewOriginalEmail = order.intakeMetadata !== null;

    const handleJobCardPdfTypeSelect = (recipientType: JobCardPdfRecipientType) => {
      setIsJobCardPdfTypeDialogOpen(false);
      onDownloadJobCardPdfsClick?.(recipientType);
    };

    const handleLoadDigestView = (loadFilter: LoadDigestLoadFilter) => {
      setIsLoadDigestViewDialogOpen(false);
      onViewLoadDigestClick?.(loadFilter);
    };

    const availableActions = useMemo(() => {
      const actions = [];

      if (canTextDrivers) {
        actions.push({
          key: 'text_all_drivers',
          icon: <SmsOutlined />,
          label: `${t('dispatch.order.text_all_drivers')}`,
          onClick: onTextAllDriversClick,
        });
      }

      if (canMessageAllDrivers) {
        actions.push({
          key: 'message_all_drivers',
          icon: <ForumOutlined />,
          label: t('messaging.message_all_drivers'),
          onClick: onOpenMessagingTabClick,
        });
      }

      if (isCloneable) {
        actions.push({
          key: 'clone_order',
          icon: <FileCopyOutlined />,
          label: `${t('dispatch.order.clone_order')}`,
          onClick: onCloneOrderClick,
        });

        actions.push({
          key: 'copy_and_edit',
          icon: <EditOutlined />,
          label: `${t('dispatch.order.copy_and_edit')}`,
          onClick: onCopyAndEditClick,
        });
      }

      if (onDownloadFilesClick) {
        actions.push({
          key: 'download_files',
          icon: <FileDownloadOutlined />,
          label: t('dispatch.order.download_files'),
          onClick: onDownloadFilesClick,
        });
      }

      if (canDownloadJobCardPdfs) {
        actions.push({
          key: 'download_job_card_pdfs',
          ...getDownloadJobCardPdfsContent(),
          onClick: () => setIsJobCardPdfTypeDialogOpen(true),
        });
      }

      if (shouldShowViewLoadDigestAction) {
        actions.push({
          key: 'view_load_digest',
          icon: <PictureAsPdfOutlined />,
          label: t('dispatch.load_digest.view_title'),
          onClick: () => setIsLoadDigestViewDialogOpen(true),
        });
      }

      if (shouldShowEmailLoadDigestAction) {
        actions.push({
          key: 'email_load_digest',
          icon: <EmailOutlined />,
          label: t('dispatch.load_digest.email_title'),
          onClick: onEmailLoadDigestClick,
        });
      }

      if (canViewOriginalEmail) {
        actions.push({
          key: 'view_original_email',
          icon: <DraftsOutlined />,
          label: t('dispatch.order.original_email.view'),
          onClick: () => setIsOriginalEmailDialogOpen(true),
        });
      }

      if (isCancellable) {
        actions.push({
          key: 'cancel_order',
          icon: <CancelOutlined />,
          label: t('dispatch.order.cancel_order'),
          onClick: onCancelClick,
        });
      }

      return actions;
    }, [
      isCloneable,
      canAddNewJobs,
      canMessageAllDrivers,
      canTextDrivers,
      canDownloadJobCardPdfs,
      canViewOriginalEmail,
      shouldShowEmailLoadDigestAction,
      shouldShowViewLoadDigestAction,
      isCancellable,
      onCancelClick,
      onCloneOrderClick,
      onCopyAndEditClick,
      onDownloadFilesClick,
      onEmailLoadDigestClick,
      onOpenMessagingTabClick,
      onTextAllDriversClick,
    ]);

    const [[cancelAction], otherActions] = partition(availableActions, (action) => {
      return action.key === 'cancel_order';
    });

    if (!availableActions.length) {
      return null;
    }

    return (
      <>
        <Menu
          ref={menuHandler}
          menuTrigger={
            <SmallButton
              color="brandV2TreadGray3"
              data-test-id={`page-dispatch__order-${order.orderId}__three-dots-menu`}
              variant="text"
              startIcon={<MoreHoriz />}
              sx={{
                [`&.${buttonBaseClasses.root}`]: {
                  border: 'solid 1px transparent',
                  borderRadius: '50%',
                  height: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                  minWidth: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                  p: 0,
                  width: NEW_DISPATCH_TOPBAR_CONTROLS_HEIGHT_IN_PX,
                  '&:hover': { borderColor: theme.brandV2.colors.treadGray3 },
                  [`.${buttonClasses.startIcon}`]: { mx: 0 },
                },
              }}
            />
          }
        >
          {otherActions.map((action) => (
            <MenuItem
              key={action.key}
              onClick={(event) => {
                event.stopPropagation();
                action.onClick();
                menuHandler.current?.onClose?.();
              }}
              data-test-id={`page-dispatch__order-${order.orderId}__menu-item-${action.key}`}
              data-ui={`page-dispatch__trucks-summary__order-${order.orderId}__button`}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  width: '100%',
                  gap: 1,
                }}
              >
                {cloneElement(action.icon, {
                  sx: { fontSize: '16px', color: theme.brandV2.colors.treadBlack },
                })}

                <Typography
                  color={theme.brandV2.colors.treadBlack}
                  sx={{
                    fontSize: '12px',
                  }}
                >
                  {action.label}
                </Typography>
              </Box>
            </MenuItem>
          ))}
          {Boolean(otherActions.length && cancelAction) && <Divider sx={{ mx: 1 }} />}
          {Boolean(cancelAction) && (
            <MenuItem
              onClick={(event) => {
                event.stopPropagation();
                cancelAction.onClick();
                menuHandler.current?.onClose?.();
              }}
              data-test-id={`page-dispatch__order-${order.orderId}__menu-item-${cancelAction.key}`}
              data-ui={`page-dispatch__trucks-summary__order-${order.orderId}__button`}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  width: '100%',
                  gap: 1,
                }}
              >
                {cloneElement(cancelAction.icon, {
                  sx: { fontSize: '16px', color: theme.brandV2.colors.treadBlack },
                })}

                <Typography
                  color={theme.brandV2.colors.treadBlack}
                  sx={{
                    fontSize: '12px',
                  }}
                >
                  {cancelAction.label}
                </Typography>
              </Box>
            </MenuItem>
          )}
        </Menu>

        <JobCardPdfTypeDialog
          isOpen={isJobCardPdfTypeDialogOpen}
          onClose={() => setIsJobCardPdfTypeDialogOpen(false)}
          onSelect={handleJobCardPdfTypeSelect}
        />

        <LoadDigestViewDialog
          isOpen={isLoadDigestViewDialogOpen}
          onClose={() => setIsLoadDigestViewDialogOpen(false)}
          onView={handleLoadDigestView}
        />

        <OriginalEmailDialog
          isOpen={isOriginalEmailDialogOpen}
          onClose={() => setIsOriginalEmailDialogOpen(false)}
          orderId={order.id}
        />
      </>
    );
  },
);

export function Requester({ order }: OrderDispatchCardFragment) {
  const theme = useTheme();
  const requester = order.requester?.fullName;

  if (!requester) {
    return null;
  }

  return (
    <Tooltip title={requester}>
      <OverflowAwareText sx={{ fontSize: '14px' }}>
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: theme.brandV2.colors.treadGray1,
            fontWeight: 600,
          }}
        >
          {t('dispatch.dispatch_v3.requestor')}:
        </Typography>{' '}
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: theme.brandV2.colors.treadBlack,
            fontWeight: 400,
          }}
        >
          {requester}
        </Typography>
      </OverflowAwareText>
    </Tooltip>
  );
}

export function StartTime({ order }: OrderDispatchCardFragment) {
  const theme = useTheme();

  const date = formatOrderStartTime(order);

  if (!date) {
    return null;
  }

  return (
    <Tooltip title={date}>
      <OverflowAwareText sx={{ fontSize: '14px' }}>
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: theme.brandV2.colors.treadGray1,
            fontWeight: 600,
          }}
        >
          {t('dispatch.dispatch_v2.start')}:
        </Typography>{' '}
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: theme.brandV2.colors.treadBlack,
            fontWeight: 400,
          }}
        >
          {date}
        </Typography>
      </OverflowAwareText>
    </Tooltip>
  );
}

export function EquipmentType({ order }: OrderDispatchCardFragment) {
  const theme = useTheme();

  if (!order.equipmentType?.name) {
    return null;
  }

  return (
    <Tooltip title={order.equipmentType.name}>
      <OverflowAwareText sx={{ fontSize: '14px' }}>
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: theme.brandV2.colors.treadGray1,
            fontWeight: 600,
          }}
        >
          {t('dispatch.order.order_details_v2.truck_type')}:
        </Typography>{' '}
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: theme.brandV2.colors.treadBlack,
            fontWeight: 400,
          }}
        >
          {order.equipmentType.name}
        </Typography>
      </OverflowAwareText>
    </Tooltip>
  );
}

interface ContactProps {
  entity: Order | Project;
}

export function Contact({ entity }: ContactProps) {
  const theme = useTheme();
  const orderSearchHighlightTerm = useOrderSearchHighlightTerm();
  const allContacts = useMemo(() => {
    let contacts: Array<{
      label: string;
      contacts: Array<{ text: string; phone?: string }>;
    }> = [];

    if (entity.foremen.length) {
      contacts = contacts.concat({
        label: t('form_fields.foreman'),
        contacts: entity.foremen.map((contact) => ({
          text: formatContactText(contact.fullName, contact.phone),
          phone: contact.phone ?? undefined,
        })),
      });
    }

    if (entity.collaborators.length) {
      contacts = contacts.concat({
        label: t('form_fields.collaborator'),
        contacts: entity.collaborators.map((contact) => ({
          text: formatContactText(contact.fullName, contact.phone),
          phone: contact.phone ?? undefined,
        })),
      });
    }

    if (entity.salesContact) {
      contacts = contacts.concat({
        label: `${t('form_fields.contact_salesman')} ${t('common.contact')}`,
        contacts: [entity.salesContact].map((contact) => ({
          text: formatContactText(contact.name, contact.phone),
          phone: contact.phone ?? undefined,
        })),
      });
    }

    if (entity.foremanContact) {
      contacts = contacts.concat({
        label: `${t('form_fields.contact_foreman')} ${t('common.contact')}`,
        contacts: [entity.foremanContact].map((contact) => ({
          text: formatContactText(contact.name, contact.phone),
          phone: contact.phone ?? undefined,
        })),
      });
    }

    if (entity.collaboratorContact) {
      contacts = contacts.concat({
        label: `${t('form_fields.contact_collaborator')} ${t('common.contact')}`,
        contacts: [entity.collaboratorContact].map((contact) => ({
          text: formatContactText(contact.name, contact.phone),
          phone: contact.phone ?? undefined,
        })),
      });
    }

    if (entity.supervisorContact) {
      contacts = contacts.concat({
        label: `${t('form_fields.contact_supervisor')} ${t('common.contact')}`,
        contacts: [entity.supervisorContact].map((contact) => ({
          text: formatContactText(contact.name, contact.phone),
          phone: contact.phone ?? undefined,
        })),
      });
    }

    return contacts;
  }, [entity]);

  const firstContact = allContacts?.[0]?.contacts?.[0];
  const contactTooltipTitle = (
    <>
      {allContacts.map((entry, entryIndex) =>
        entry.contacts.map((contact, contactIndex) => (
          <React.Fragment key={`${entry.label}-${contact.text}`}>
            {entryIndex > 0 || contactIndex > 0 ? <br /> : null}
            {entry.label} {'•'}{' '}
            <HighlightedText searchTerm={orderSearchHighlightTerm} text={contact.text} />
          </React.Fragment>
        )),
      )}
    </>
  );

  const copyToClipboard = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      navigator.clipboard.writeText(firstContact?.phone ?? '');
    },
    [firstContact?.phone],
  );

  if (!allContacts.length) {
    return null;
  }

  return (
    <Tooltip
      onClick={copyToClipboard}
      placement="bottom-start"
      title={contactTooltipTitle}
      slotProps={{
        popper: { modifiers: [{ name: 'offset', options: { offset: [0, -14] } }] },
        tooltip: {
          sx: {
            [`&.${tooltipClasses.tooltip}`]: {
              maxWidth: '600px',
              whiteSpace: 'pre-line',
            },
          },
        },
      }}
    >
      <OverflowAwareText sx={{ fontSize: '14px' }}>
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: theme.brandV2.colors.treadBlack,
            fontWeight: 600,
          }}
        >
          {allContacts[0].label}:
        </Typography>{' '}
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: theme.brandV2.colors.treadBlack,
            fontWeight: 400,
          }}
        >
          <HighlightedText
            searchTerm={orderSearchHighlightTerm}
            text={firstContact.text}
          />
        </Typography>
      </OverflowAwareText>
    </Tooltip>
  );
}

interface BaseNotesComponentProps {
  text?: string;
  title: string;
}

function BaseNotesComponent({ text, title }: BaseNotesComponentProps) {
  const theme = useTheme();

  if (!text) {
    return null;
  }

  return (
    <Tooltip
      placement="bottom-start"
      title={`${title}: ${text}`}
      slotProps={{
        tooltip: { sx: { [`&.${tooltipClasses.tooltip}`]: { whiteSpace: 'pre-line' } } },
      }}
    >
      <OverflowAwareText sx={{ fontSize: '14px' }}>
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: theme.brandV2.colors.treadGray1,
            fontWeight: 600,
          }}
        >
          {title}:
        </Typography>{' '}
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: theme.brandV2.colors.treadBlack,
            fontWeight: 400,
          }}
        >
          {text}
        </Typography>
      </OverflowAwareText>
    </Tooltip>
  );
}

export function OrderNotes({ order }: OrderDispatchCardFragment) {
  const text = getOrderNotes(order, 'orderNotes');

  return <BaseNotesComponent text={text} title={t('form_fields.order')} />;
}

export function InternalNotes({ order }: OrderDispatchCardFragment) {
  const text = getOrderNotes(order, 'internalNotes');

  return <BaseNotesComponent text={text} title={t('form_fields.internal')} />;
}

export function JobNotes({ order }: OrderDispatchCardFragment) {
  const text = getOrderNotes(order, 'jobNotes');

  return <BaseNotesComponent text={text} title={t('form_fields.driver')} />;
}

export function CycleTime({ order }: OrderDispatchCardFragment) {
  const theme = useTheme();

  if (!order.loadCycleAvg) {
    return null;
  }

  return (
    <OverflowAwareText sx={{ fontSize: '14px' }}>
      <Typography
        component="span"
        sx={{
          fontSize: 'inherit',
          color: theme.brandV2.colors.treadGray1,
          fontWeight: 600,
        }}
      >
        {t('dispatch.dispatch_v2.cycle_time')}:
      </Typography>{' '}
      <Typography
        component="span"
        sx={{
          fontSize: 'inherit',
          color: theme.brandV2.colors.treadBlack,
          fontWeight: 400,
        }}
      >
        {getJobCycleTimeText(order)}
      </Typography>
    </OverflowAwareText>
  );
}

export function Alerts({ order }: OrderDispatchCardFragment) {
  const theme = useTheme();
  let hasNonRoutableSites = false;

  if (order.waypoints) {
    hasNonRoutableSites = order.waypoints.some((waypoint) => {
      return !waypoint.siteNested?.routable;
    });
  }

  let alertMessage = '';

  if (hasNonRoutableSites) {
    alertMessage = t('common.address_confirmation_required');
  } else if (order.actionRequired) {
    alertMessage = t('dispatch.order.pending_jobs');
  }

  if (!alertMessage) {
    return null;
  }

  return (
    <Tooltip title={alertMessage}>
      <WarningRounded sx={{ color: theme.brandV2.colors.treadOrange }} />
    </Tooltip>
  );
}

export const TrucksSummary = observer(({ order }: OrderDispatchCardFragment) => {
  const { ordersDispatchStore } = useStores();

  const isWaitingForTrucksCountMatch =
    ordersDispatchStore.ordersWaitingForTrucksCountToMatchCountInSummary.has(order.id);

  const widget = useMemo(() => {
    const {
      accepted: count,
      acceptedText: bottomRow,
      total,
    } = getOrderTrucksProgress(order, isWaitingForTrucksCountMatch);
    const topRow = t('common.trucks_count', {
      count: total,
      plural: total > 1 ? 's' : '',
    });

    return { topRow, bottomRow, count, total };
  }, [order, isWaitingForTrucksCountMatch]);

  return (
    <OrderProgressWidget
      count={widget.count}
      primaryText={widget.topRow}
      secondaryText={widget.bottomRow}
      total={widget.total}
      label={t('dispatch.dispatch_v3.trucks_summary.label_without_number')}
    />
  );
});

export function DeliveredLoadsSummary({ order }: OrderDispatchCardFragment) {
  const { deliveredLoadsCount, deliveredQuantityText, plannedText, plannedTotal } =
    getOrderDeliveredLoads(order);

  const TooltipContent = () => {
    return (
      <>
        <Typography variant="body1">
          {t('dispatch.dispatch_v3.tooltip.delivered_loads')}: {deliveredLoadsCount}
        </Typography>
        <Typography variant="body1">
          {t('dispatch.dispatch_v3.tooltip.delivered_quantity')}*: {deliveredQuantityText}
        </Typography>
        <br />
        <Typography variant="body1">
          {t('dispatch.dispatch_v3.tooltip.estimation_disclaimer')}
        </Typography>
      </>
    );
  };

  const bottomRow = (
    <Tooltip title={<TooltipContent />}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <OverflowAwareText
          sx={{
            fontSize: '12px',
            fontWeight: 500,
            lineHeight: '21px',
          }}
        >
          <CheckCircleOutlineOutlined
            sx={{ fontSize: '12px', height: 16, width: 16, mr: 0.5 }}
          />
          {deliveredLoadsCount}
          {deliveredQuantityText ? (
            <>
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '21px',
                }}
              >
                {` • `}
                {deliveredQuantityText}{' '}
              </span>
            </>
          ) : null}
        </OverflowAwareText>
      </Box>
    </Tooltip>
  );

  return (
    <FlexColumn>
      <OrderProgressWidget
        count={deliveredLoadsCount}
        primaryText={plannedText}
        secondaryText={bottomRow}
        total={plannedTotal}
        label={t('dispatch.dispatch_v3.loads_summary.label')}
      />
    </FlexColumn>
  );
}

// Show the first N material lines inline and put the rest behind a "+N more"
// expander, so a 4-5 material order doesn't blow up the dispatch card height.
const MATERIAL_SUMMARY_INLINE_CAP = 3;

// Below this share of the ordered quantity a row's bar turns amber instead of green,
// so a material that is lagging reads as lagging at a glance on a dense board.
const MATERIAL_PROGRESS_WARN_RATIO = 0.5;

// One per-material row, shared by the inline rows and the "+N more" reveal.
// Reads "<delivered> / <ordered> <unit>" over a progress bar: the ordered figure is
// the dispatcher's own entry, the delivered figure is summed from the order's loads.
function MaterialTotalRow({
  name,
  delivered,
  quantity,
  unitOfMeasure,
}: {
  name: string;
  delivered: number | null;
  quantity: string;
  unitOfMeasure: string | null;
}) {
  const theme = useTheme();

  const {
    delivered: deliveredLabel,
    orderedQuantity,
    orderedWithUnit,
  } = formatMaterialRowLabels(delivered, quantity, unitOfMeasure);

  const progressRatio =
    orderedQuantity && orderedQuantity > 0 && !isNil(delivered)
      ? Math.min(1, delivered / orderedQuantity)
      : null;

  return (
    <Box data-test-id="order-material-total-row">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 0.5,
        }}
      >
        {/*
          The name ellipsizes at whatever width the quantity beside it leaves, and
          material names run long, so the full value is always available on hover.
          Deliberately unconditional: gating it on a measured overflow needs layout
          metrics jsdom does not provide, and it would go stale on re-flow — a hover
          that repeats a fully visible name is the cheaper trade.
        */}
        <Tooltip title={name}>
          <OverflowAwareText
            data-test-id="order-material-total-name"
            sx={{
              fontSize: '12px',
              fontWeight: 500,
              // 21px matches the line box of the Trucks/Loads values beside this, so
              // the first material row's bar lands on the same line as their bars.
              lineHeight: '21px',
              color: theme.brandV2.colors.treadGray3,
              // Take every pixel the nowrap quantity beside it does not need, rather
              // than splitting the row evenly with it. minWidth 0 is what lets a flex
              // child actually shrink to its ellipsis instead of its content width.
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            {name}
          </OverflowAwareText>
        </Tooltip>
        <OverflowAwareText
          data-test-id="order-material-total-quantity"
          sx={{
            fontSize: '12px',
            lineHeight: '21px',
            whiteSpace: 'nowrap',
            color: theme.brandV2.colors.treadGray3,
            // The figures must never ellipsize, so this keeps its natural width and
            // the name absorbs the remainder.
            flex: '0 0 auto',
            width: 'auto',
          }}
        >
          <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {deliveredLabel}
          </Box>
          {` / ${orderedWithUnit}`}
        </OverflowAwareText>
      </Box>

      {isNil(progressRatio) ? null : (
        <Box
          data-test-id="order-material-total-progress"
          sx={{
            // Height, radius and track colour match the Trucks/Loads bars beside it
            // so the three stats read as one row of widgets.
            height: '4px',
            borderRadius: 1.2,
            // Same 4px the stat column uses between its value and its bar.
            mt: '4px',
            overflow: 'hidden',
            backgroundColor: theme.brandV2.colors.treadGray7,
          }}
        >
          <Box
            data-test-id="order-material-total-progress-fill"
            sx={{
              height: '100%',
              borderRadius: 1.2,
              width: `${progressRatio * 100}%`,
              backgroundColor:
                progressRatio < MATERIAL_PROGRESS_WARN_RATIO
                  ? theme.brandV2.colors.treadOrange
                  : theme.brandV2.colors.treadGreen,
            }}
          />
        </Box>
      )}
    </Box>
  );
}

// Multi-material "Materials Ordered" summary. Every row carries its own unit and its
// own bar — there is deliberately no blended header total, because the units can
// differ (you can't sum tons and loads) and a rolled-up figure squeezed the heading
// into an ellipsis on a real board.
function MultiMaterialOrderedSummary({ order }: OrderDispatchCardFragment) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  const deliveredByMaterialAndUnit = useMemo(
    () => getDeliveredByMaterialAndUnit(order),
    [order],
  );

  const rows = useMemo(
    () =>
      getOrderMaterialRows(order).map((row) => ({
        ...row,
        // Nothing delivered in THIS row's unit means no figure to show — which is
        // different from a delivered zero, and different again from deliveries that
        // exist only in some other unit.
        delivered: getDeliveredForMaterialRow(deliveredByMaterialAndUnit, row),
      })),
    [order, deliveredByMaterialAndUnit],
  );

  const visibleRows = expanded ? rows : rows.slice(0, MATERIAL_SUMMARY_INLINE_CAP);
  const hiddenCount = rows.length - MATERIAL_SUMMARY_INLINE_CAP;
  const toggleExpanded = useCallback(() => setExpanded((value) => !value), []);

  return (
    // Two things share this row: a material name that wants as much room as it can
    // get, and "<delivered> / <ordered> <unit>" which is nowrap and so cannot give
    // any back. 220px leaves the name ~125px — enough for the realistic names that
    // were clipping at the old width — and the tooltip covers the long tail rather
    // than widening further at the expense of the field grid to the left. Only the
    // multi-material variant widens; a single-material card is left as it was.
    <FlexColumn sx={{ gap: 0.5, minWidth: '220px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
        }}
      >
        <OverflowAwareText
          sx={{
            // Matches the label on the Trucks/Loads widgets beside this one
            // (OrderProgressWidget) so all three headings sit on one line at one size.
            fontSize: '8px',
            fontWeight: 400,
            textTransform: 'uppercase',
            color: theme.brandV2.colors.treadGray1,
          }}
        >
          {t('dispatch.dispatch_v3.delivered_quantities_widget.label_plural')}
        </OverflowAwareText>
      </Box>

      {visibleRows.map((row, index) => (
        <Box
          key={row.id}
          sx={{
            // A dashed rule separates the stacked rows without reading as a table;
            // the padding keeps the following row off the rule.
            ...(index > 0
              ? {
                  borderTop: `1px dashed ${theme.brandV2.colors.treadGray7}`,
                  pt: '4px',
                }
              : {}),
          }}
        >
          <MaterialTotalRow
            name={row.label}
            delivered={row.delivered}
            quantity={row.quantity}
            unitOfMeasure={row.unitOfMeasure}
          />
        </Box>
      ))}

      {rows.length > MATERIAL_SUMMARY_INLINE_CAP ? (
        // Styled text rather than a Button to match the dispatch card's density, so
        // the button role, focus stop and Enter/Space handling have to be supplied
        // by hand — without them the hidden materials are unreachable by keyboard.
        // Space scrolls the board unless its default is suppressed.
        <OverflowAwareText
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={toggleExpanded}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggleExpanded();
            }
          }}
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
            color: theme.brandV2.colors.treadBlue,
          }}
        >
          {expanded
            ? t('dispatch.dispatch_v3.delivered_quantities_widget.show_less')
            : t('dispatch.dispatch_v3.delivered_quantities_widget.show_more', {
                count: hiddenCount,
              })}
        </OverflowAwareText>
      ) : null}
    </FlexColumn>
  );
}

export function DeliveredQuantitiesSummary({ order }: OrderDispatchCardFragment) {
  const theme = useTheme();
  const { userStore } = useStores();

  const isOrderCreator = order.company?.id === userStore.userCompany.id;
  const isOrderCustomer =
    order.account?.connectedCompany?.id === userStore.userCompany.id;
  const deliveredMaterialUom =
    order.orderSummary?.materialGrossUnitOfMeasure?.name ?? null;

  const {
    ticketedQuantity: usedDeliveredQuantity,
    ticketedQuantityText,
    ticketsCount,
  } = getOrderTicketsSummary(order);

  const widget = useMemo(() => {
    const topRowTotal = isNil(order.quantity) ? null : Number(order.quantity);
    const orderedQuantityText = makeOrderedQuantityText(
      order.quantity,
      order.unitOfMeasure?.id,
    );

    const TooltipContent = (topRowTotal: string) => {
      return (
        <>
          <Typography variant="body1">
            {t('dispatch.dispatch_v3.tooltip.ordered_quantity')}: {topRowTotal}
          </Typography>
          <Typography variant="body1">
            {t('dispatch.dispatch_v3.tooltip.delivered_quantity')}:{' '}
            {formatNumber(usedDeliveredQuantity)} {deliveredMaterialUom}
          </Typography>
          <Typography variant="body1">
            {t('dispatch.dispatch_v3.tooltip.tickets')}: {ticketsCount}
          </Typography>
        </>
      );
    };

    const bottomRow = (topRowTotal: string) => {
      return (
        <Tooltip title={TooltipContent(topRowTotal)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <OverflowAwareText
              sx={{
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '21px',
              }}
            >
              {ticketedQuantityText ? (
                <>
                  <span
                    style={{
                      fontSize: '12px',
                      fontWeight: 500,
                      lineHeight: '21px',
                    }}
                  >
                    {ticketedQuantityText}{' '}
                    <ReceiptOutlined sx={{ fontSize: '12px', height: 16, width: 16 }} />
                    {ticketsCount}
                  </span>
                </>
              ) : null}
              {/* <ReceiptOutlined
                sx={{ fontSize: '12px', height: 16, width: 16, mr: 0.5 }}
              />
              {ticketsCount} */}
            </OverflowAwareText>
          </Box>
        </Tooltip>
      );
    };

    const topRow = orderedQuantityText ?? (
      <OverflowAwareText
        sx={{
          color: theme.brandV2.colors.treadGray4,
          fontSize: '12px',
          fontWeight: 500,
          lineHeight: '21px',
        }}
      >
        {t('dispatch.dispatch_v3.delivered_quantities_widget.empty_top_row')}
      </OverflowAwareText>
    );

    return {
      topRow: order.editable ? topRow : '-',

      bottomRow: bottomRow(orderedQuantityText ?? '-'),
      count: usedDeliveredQuantity,
      total: topRowTotal ?? 0,
    };
  }, [isOrderCreator, isOrderCustomer, order]);

  // A single scalar material renders unchanged; two or more show the capped,
  // stacked per-material list.
  //
  // An order the viewing company cannot edit keeps the scalar widget whatever its
  // material count, because that widget masks the ordered quantity behind a dash
  // (see topRow above) and the per-material list has no such masking. A company
  // that only receives an order must not start seeing the customer's ordered
  // amounts just because the order carries more than one material.
  const isMultiMaterial = orderShowsMaterialList(order);

  return (
    <FlexColumn>
      {isMultiMaterial ? (
        <MultiMaterialOrderedSummary order={order} />
      ) : (
        <OrderProgressWidget
          count={widget.count}
          primaryText={widget.topRow}
          secondaryText={widget.bottomRow}
          total={widget.total}
          label={t('dispatch.dispatch_v3.delivered_quantities_widget.label')}
        />
      )}
    </FlexColumn>
  );
}

interface BaseWaypointComponentProps {
  entity: Order | Project;
  waypointType: Extract<WaypointType, WaypointType.PICKUP | WaypointType.DROP_OFF>;
}

function BaseWaypointComponent({ entity, waypointType }: BaseWaypointComponentProps) {
  const theme = useTheme();
  const orderSearchHighlightTerm = useOrderSearchHighlightTerm();
  const props = useMemo(() => {
    const isPickup = waypointType === WaypointType.PICKUP;
    const waypointArray = isPickup ? entity.pickUpWayPoints : entity.dropOffWayPoints;
    const waypoint = extractWaypointData(waypointArray?.[0]);

    if (!waypoint) {
      return null;
    }

    if (isPickup) {
      return { waypoint, label: t('form_fields.pickup') };
    }

    return { waypoint, label: t('form_fields.dropoff') };
  }, [entity, waypointType]);

  if (!props) {
    return null;
  }

  return (
    <OverflowAwareText
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '14px' }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 'inherit',
          color: theme.brandV2.colors.treadGray1,
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {props.label}:
      </Typography>{' '}
      <GeofenceStartAdornment geofenceType={props.waypoint.geofenceType} />
      <Tooltip
        title={
          <>
            <HighlightedText
              searchTerm={orderSearchHighlightTerm}
              text={props.waypoint.name}
            />
            {props.waypoint.fullAddress && (
              <>
                <br />
                <HighlightedText
                  searchTerm={orderSearchHighlightTerm}
                  text={props.waypoint.fullAddress}
                />
              </>
            )}
          </>
        }
      >
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            color: theme.brandV2.colors.treadBlack,
            fontWeight: 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
            flex: 1,
          }}
        >
          <HighlightedText
            searchTerm={orderSearchHighlightTerm}
            text={props.waypoint.name}
          />
        </Typography>
      </Tooltip>
    </OverflowAwareText>
  );
}

export function PickupWaypoint({ entity }: { entity: Order | Project }) {
  return <BaseWaypointComponent entity={entity} waypointType={WaypointType.PICKUP} />;
}

export function DropffWaypoint({ entity }: { entity: Order | Project }) {
  return <BaseWaypointComponent entity={entity} waypointType={WaypointType.DROP_OFF} />;
}
